import Route from '@ember/routing/route';
import { get } from '@ember/object';
import { service } from '@ember/service';
import { hash } from 'rsvp';
import { routeEvent } from 'garaje/utils/decorators/route';

export default class extends Route {
  @service transitionConfirm;
  @service store;
  @service featureFlags;
  @service('sensitive-data-categories') sensitiveDataCategories;

  queryParams = {
    showFieldOptions: {
      replace: true,
    },
  };

  async model() {
    const model = this.modelFor('location-overview.sign-in-flows.show');

    // Load current sensitive data categories for the Sensitive fields tab
    // (dedicated v3 endpoint, decoupled from field_settings). Gated on the same
    // flag as the tab so flag-off customers pay no extra request, and resolved
    // via hash() alongside the rule-group query rather than serially blocking
    // first paint. Non-fatal: a failure must not take down the page.
    const sensitiveDataCategories = this.featureFlags.isEnabled('sensitive-fields')
      ? this.sensitiveDataCategories.load(model.globalFlow.id).catch((error) => {
          // eslint-disable-next-line no-console
          console.warn('Failed to load sensitive data categories:', error);
        })
      : undefined;

    const signInFieldPageId = get(model, 'globalFlow.signInFieldPage.id');
    const includes = ['sign-in-field-actions', 'actionable-sign-in-field-actions', 'actionable-sign-in-fields'];
    const signInFieldPage = await this.store.findRecord('global-sign-in-field-page', signInFieldPageId, {
      reload: true,
      include: includes.map((include) => `sign-in-fields.${include}`).join(','),
    });
    const signInFieldActionRuleGroups = this.store.query('global-sign-in-field-action-rule-group', {
      filter: { flow: model.globalFlow.id },
      page: { limit: 200 },
      include: 'sign-in-field-actions,sign-in-field-actions-contacts,sign-in-field-actions-contacts.employee',
    });

    return hash({
      signInFields: get(signInFieldPage, 'signInFields'),
      signInFieldActionRuleGroups,
      sensitiveDataCategories,
      globalFlow: model.globalFlow,
      locations: model.locations,
      visualComplianceConfiguration: get(model.globalFlow, 'visualComplianceConfiguration'),
    });
  }

  @routeEvent
  routeWillChange(transition) {
    const changeset = get(this.controller, 'changeset');
    const confirmTask = get(this, 'transitionConfirm.displayConfirmTask');
    const sensitiveDataCategories = this.sensitiveDataCategories;
    const flowId = this.modelFor('location-overview.sign-in-flows.show')?.globalFlow?.id;
    const categoriesDirty = sensitiveDataCategories.isDirty(flowId);

    // Category dirtiness stands on its own — it lives in the service, not the
    // changeset, so don't gate the prompt on a registered changeset being dirty.
    if ((changeset && get(changeset, 'isDirty')) || categoriesDirty) {
      confirmTask.perform(transition, {
        continue() {
          if (changeset) changeset.rollbackAttributes();
          if (flowId) sensitiveDataCategories.rollback(flowId);
        },
      });
    }
  }
}
